import React, { useEffect, useMemo, useRef, useState } from 'react';
import CardCommon from '@/pages/strategic/components/CardCommon';
import { TYPES } from '@sg/pages/StudioManagement/InDevProjects/pages/InDevProjectsAll/const';
import StrategicImageNew from '@sg/components/StrategicImageNew';
import { Bubble } from 'tea-component';

interface IOverviewCardProps {
  active?: boolean;
  type: string;
  page?: string;
  onClick?: () => void;
  projects?: any[];
}

interface IMetricBlockProps {
  data?: any[];
  title: string;
  titleColor?: string;
}

const CLS = 'dbs-lto-overview-card';
// icon 列表中单个图标尺寸 + 间距（与 SCSS 保持一致）
const ICON_SIZE = 16;
const ICON_GAP = 4;
// 末尾 +N 标识的预留宽度（保守取最大场景 +99，对应 SCSS 中 min-width 22 + padding 8 + 字符宽度）
const MORE_SLOT_WIDTH = 40;

// metric 内部 icon 列表：容器宽度不够时末尾用 +N div 代替超出的图标
const MetricBlock: React.FC<IMetricBlockProps> = ({ data = [], title, titleColor }) => {
  const listRef = useRef<HTMLDivElement>(null);
  const [visibleIconCount, setVisibleIconCount] = useState<number>(data?.length || 0);

  useEffect(() => {
    const total = data?.length || 0;
    const el = listRef.current;
    if (!el || total === 0) {
      setVisibleIconCount(total);
      return;
    }

    const measure = () => {
      const containerWidth = el.offsetWidth;
      const slot = ICON_SIZE + ICON_GAP;
      // 不溢出场景：全部 icon 占用 = total*ICON_SIZE + (total-1)*ICON_GAP
      const fullWidth = total * ICON_SIZE + Math.max(0, total - 1) * ICON_GAP;
      if (fullWidth <= containerWidth) {
        setVisibleIconCount(total);
        return;
      }
      // 溢出场景：末尾给 +N 预留 MORE_SLOT_WIDTH，其余空间放 icon
      const remainForIcons = containerWidth - MORE_SLOT_WIDTH - ICON_GAP;
      const iconCount = Math.max(0, Math.floor((remainForIcons + ICON_GAP) / slot));
      setVisibleIconCount(iconCount);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [data]);

  const total = data?.length || 0;
  const hiddenIcons = Math.max(0, total - visibleIconCount);

  return (
    <div className={`${CLS}__metric`}>
      <div className={`${CLS}__metric-title`} style={titleColor ? { color: titleColor } : undefined}>
        {title}
      </div>
      <div className={`${CLS}__metric-row`}>
        <div className={`${CLS}__metric-game-length`}>{total > 0 ? total : '-'}</div>
        <div className={`${CLS}__metric-game-list`} ref={listRef}>
          <Bubble
            dark
            content={
              total > 0 ? (
                <div
                  className={`${CLS}__metric-tip${total > 4 ? ` ${CLS}__metric-tip--scrollable` : ''}`}
                >
                  {data.map((item: any, idx: number) => (
                    <span key={item?.unified_id || item?.game_title || idx} className={`${CLS}__metric-tip-item`}>
                      <StrategicImageNew
                        key={item?.unified_id || item?.game_title || idx}
                        domain={item?.domain}
                        width={ICON_SIZE}
                        height={ICON_SIZE}
                        className={`${CLS}__metric-game-icon`}
                        src={item?.cover || ''}
                      />
                      <span className={`${CLS}__metric-tip-name`}>{item?.game_title || '-'}</span>
                      {idx < data.length - 1 ? <span className={`${CLS}__metric-tip-sep`}>,</span> : null}
                    </span>
                  ))}
                </div>
              ) : null
            }
          >
            <div className={`${CLS}__metric-game-list-inner`}>
              {data.slice(0, visibleIconCount).map((item: any, idx: number) => (
                <StrategicImageNew
                  key={item?.unified_id || item?.game_title || idx}
                  domain={item?.domain}
                  width={ICON_SIZE}
                  height={ICON_SIZE}
                  className={`${CLS}__metric-game-icon`}
                  src={item?.cover || ''}
                />
              ))}
              {hiddenIcons > 0 && <div className={`${CLS}__metric-game-more`}>+{hiddenIcons}</div>}
            </div>
          </Bubble>
        </div>
      </div>
    </div>
  );
};

const OverviewCard: React.FC<IOverviewCardProps> = (props) => {
  const { onClick, type, projects } = props;

  const isMc = useMemo(() => {
    return type === TYPES.mc;
  }, [type]);

  // metric 需要同时拿到“数量”与“项目列表”，这里统一返回项目数组
  const totalInDev = useMemo(() => projects?.length || 0, [projects]);

  const onTrack = useMemo(() => projects?.filter((item) => item.status === 'On Track') || [], [projects]);
  const delayed = useMemo(() => projects?.filter((item) => item.status === 'Delayed') || [], [projects]);

  const concept = useMemo(() => projects?.filter((item) => item.milestone_category === 'Concept') || [], [projects]);
  const vs = useMemo(
    () => projects?.filter((item) => item.milestone_category === 'Vertical Slice (VS)') || [],
    [projects],
  );
  const alpha = useMemo(() => projects?.filter((item) => item.milestone_category === 'Alpha') || [], [projects]);
  const beta = useMemo(() => projects?.filter((item) => item.milestone_category === 'Beta') || [], [projects]);

  const toBeLaunched = useMemo(
    () => projects?.filter((item) => item.milestone_category === 'To be Launched') || [],
    [projects],
  );

  return (
    <CardCommon
      cardKey={isMc ? 0 : 3}
      onClick={onClick}
      title={
        <div className="dbs-metric-card-title-wrapper">
          <span>{isMc ? `This Month's Milestones` : 'Milestone Distribution'}</span>
        </div>
      }
      className="dbs-home-metric-card"
    >
      <div className={`${CLS}__body`}>
        <div className={`${CLS}__metrics`}>
          {isMc ? (
            <div className={`${CLS}__metrics_tmm`}>
              <div className={`${CLS}__metric ${CLS}__total`} key={'total_in_dev'}>
                <div className={`${CLS}__total-label`}>{'Total In Dev'}</div>
                <div className={`${CLS}__total-value`}>{totalInDev ?? '-'}</div>
              </div>
              <MetricBlock key={'on_track'} data={onTrack} title={'On Track'} titleColor={'#29b569'} />
              <MetricBlock key={'delayed'} data={delayed} title={'Delayed'} titleColor={'#ef5858'} />
            </div>
          ) : (
            <div className={`${CLS}__metrics_md`}>
              <MetricBlock key={'concept'} data={concept} title={'Concept'} />
              <MetricBlock key={'vs'} data={vs} title={'Vertical Slice'} />
              <MetricBlock key={'alpha'} data={alpha} title={'Alpha'} />
              <MetricBlock key={'beta'} data={beta} title={'Beta'} />
              <MetricBlock key={'to_be_launched'} data={toBeLaunched} title={'To Be Launched'} />
            </div>
          )}
        </div>
      </div>
    </CardCommon>
  );
};

export default OverviewCard;
